import { useEffect, useState, type JSX } from "react";

import { Pagination } from "@/shared/components/Pagination";

import { useListBundles } from "../api/use-list-bundles";
import type { Marketplace } from "../lib/url-state";

import { BundleFormModal } from "./BundleFormModal";
import { DeleteBundleConfirm } from "./DeleteBundleConfirm";
import { InventoryTable, type InventoryRow } from "./InventoryTable";

const DEFAULT_LIMIT = 50;

interface BundlesPanelProps {
  /** Surfaces the bundle total to the parent (e.g. for the Bundles tab count). */
  onTotalChange?: (total: number) => void;
  /** Render the pagination control. Off in the merged "All" view, where bundles
   *  list compactly below the stock table and the stock pagination is the only one. */
  paginated?: boolean;
  /** Search text from the shared toolbar — filters by listing_code or product_name. */
  search?: string | undefined;
  /** Marketplace from the shared toolbar — filters the already per-marketplace-
   *  exploded own_stock rows client-side (no /v1/listings query param for this). */
  marketplace?: Marketplace | undefined;
  /** listing:write — gates bundle inline multiplier and row write actions. Default true. */
  canWriteListing?: boolean;
}

/**
 * In-page bundle (listing-variant) management. Renders bundle rows through the
 * unified {@link InventoryTable} (Stage 2): each listing's `own_stock` array is
 * exploded into one row per marketplace, mirroring the base-stock grid's
 * per-(sku, marketplace) row model. Creation is delegated to the page-level
 * toolbar Create button; this panel owns list, edit, delete and pagination. It
 * does NOT render a PageHeader (the Inventory page owns it).
 */
export function BundlesPanel({
  onTotalChange,
  paginated = true,
  search,
  marketplace,
  canWriteListing = true,
}: BundlesPanelProps = {}): JSX.Element {
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [editCode, setEditCode] = useState<string | null>(null);
  const [deleteCode, setDeleteCode] = useState<string | null>(null);

  // Reset to page 1 whenever the search text changes — a stale offset from a
  // larger unfiltered result set can page past a smaller filtered one. Adjusted
  // during render (React's "reset state on prop change" pattern) rather than an
  // effect, to avoid the extra render pass from calling setState in an effect.
  const [prevSearch, setPrevSearch] = useState(search);
  if (search !== prevSearch) {
    setPrevSearch(search);
    setOffset(0);
  }

  const { data, isLoading, isError, refetch } = useListBundles({ offset, limit, search });

  const total = data?.total ?? 0;
  useEffect(() => {
    onTotalChange?.(total);
  }, [total, onTotalChange]);

  // Explode each listing's own_stock into one bundle row per marketplace, so
  // bundles share the base-stock grid's per-marketplace row model (and the same
  // Marketplace column). A listing with no persisted own_stock yet yields no
  // rows — the same way a base SKU with no stock rows shows none. The
  // marketplace filter narrows this exploded set client-side (own_stock is
  // already fetched for all 3 marketplaces per listing — no extra request).
  const bundleRows: InventoryRow[] = (data?.items ?? []).flatMap((listing) =>
    (listing.own_stock ?? [])
      .filter((own) => !marketplace || own.marketplace === marketplace)
      .map((own) => {
        const row = {
          type: "bundle" as const,
          listing_code: listing.listing_code ?? "",
          own,
        };
        return listing.product_name !== undefined
          ? { ...row, product_name: listing.product_name }
          : row;
      }),
  );

  return (
    <div data-testid="bundles-panel">
      <InventoryTable
        items={bundleRows}
        isLoading={isLoading}
        isError={isError}
        onEditBundle={(code) => setEditCode(code)}
        onDeleteBundle={(code) => setDeleteCode(code)}
        onRetry={() => void refetch()}
        tableTestId="bundles-table"
        canWriteListing={canWriteListing}
        // Bundle rows never render the stock selection column. This prop only drives
        // the sticky-offset / spacer-column layout so bundle rows line up with the
        // table header, so it must track the same flag that hides the bundle
        // table's write controls.
        canWriteStock={canWriteListing}
      />

      {paginated && (
        <Pagination
          total={total}
          offset={offset}
          limit={limit}
          onChange={(next) => {
            setOffset(next.offset);
            setLimit(next.limit);
          }}
        />
      )}

      {editCode && <BundleFormModal code={editCode} onClose={() => setEditCode(null)} />}
      {deleteCode && <DeleteBundleConfirm code={deleteCode} onClose={() => setDeleteCode(null)} />}
    </div>
  );
}
